import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  ChevronLeft, ChevronRight, Clock, MapPin, Plus, CheckCircle2, ClipboardList,
} from "lucide-react";
import { format, addDays, subDays, startOfWeek } from "date-fns";
import { toast } from "sonner";
import { API } from "../../App";
import { getTechColor, techInitials } from "../../utils/techColors";
import { useTimezone } from "../../contexts/TimezoneContext";
import DispatchTaskDialog from "./DispatchTaskDialog";
import DispatchTasksPanel from "./DispatchTasksPanel";
import { JobSortToggle, readJobSortPref, writeJobSortPref } from "./JobSortToggle";

/* ─────────────────────────────────────────────────────────────────────
 * JobTimelineView
 *
 * Two sections per view:
 *   • DISPATCH — office users (dispatchers/admins) with their to-do TASKS
 *     (click an empty slot on a dispatcher's row to create a task there;
 *      click a task to edit; "Tasks" button opens the full to-do panel)
 *   • TECHNICIANS — every ACTIVE technician (always shown, even without
 *     jobs); jobs render as colored bars, drag to reschedule.
 * ───────────────────────────────────────────────────────────────────── */

const PIXELS_PER_HOUR_DAY = 110;
const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 36;
const TECH_COL_WIDTH = 180;

const ROW_PAD_TOP = 6;
const ROW_PAD_BOTTOM = 6;
const BAR_HEIGHT = ROW_HEIGHT - ROW_PAD_TOP - ROW_PAD_BOTTOM;

const DISPATCH_COLOR = "#8b5cf6";
const LANE_GAP = 4;

/** Greedy lane assignment for horizontally-overlapping bars. */
function assignLanes(items) {
  const laneEnds = [];
  const laneOf = {};
  [...items].sort((a, b) => a.left - b.left).forEach((it) => {
    let lane = laneEnds.findIndex((end) => it.left >= end);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
    laneEnds[lane] = it.left + it.width + LANE_GAP;
    laneOf[it.id] = lane;
  });
  return { laneOf, laneCount: Math.max(1, laneEnds.length) };
}

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

/** Parse "h:mm AM/PM" or "HH:mm" → minutes since midnight, or null. */
function parseTimeStr(s) {
  if (!s) return null;
  const ampm = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    if (ampm[3].toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + m;
  }
  const h24 = String(s).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (h24) return parseInt(h24[1], 10) * 60 + parseInt(h24[2], 10);
  return null;
}

/** Format minutes-since-midnight → "h:mm AM/PM" for display + storage. */
function minutesToAmPm(mins) {
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Timezone-safe day key: take the literal YYYY-MM-DD from the stored value.
 *  `new Date("2026-08-03")` / `"...T00:00:00+00:00"` parse as UTC and shift
 *  to the previous local day — which made scheduled jobs vanish. */
function dateKeyOf(d) {
  const s = String(d || "");
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  try {
    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return null;
    return format(parsed, "yyyy-MM-dd");
  } catch { return null; }
}

/** Short customer/job label for a bar — "Customer · Address line 1". */
function jobShortLabel(job) {
  const cust = job.customer_name || "Customer";
  const addr = (job.customer_address || "").split(",")[0].trim();
  return addr ? `${cust} · ${addr}` : cust;
}

function displayTime(s) {
  const mins = parseTimeStr(s);
  return mins === null ? "" : minutesToAmPm(mins);
}

const FALLBACK_TAG_COLOR = "#64748b";
const taskColor = (task, tagMap) => {
  const first = (task.tag_ids || []).map((id) => tagMap[id]).find(Boolean);
  return first?.color || FALLBACK_TAG_COLOR;
};

// ─── Task tag chips (Workiz-style) ───────────────────────────────────
const TagChips = ({ task, tagMap, max = 2 }) => {
  const tags = (task.tag_ids || []).map((id) => tagMap[id]).filter(Boolean);
  if (!tags.length) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {tags.slice(0, max).map((t) => (
        <span key={t.tag_id} className="px-1 rounded text-[9px] font-bold text-white leading-tight"
          style={{ backgroundColor: t.color }}>
          {t.name}
        </span>
      ))}
      {tags.length > max && <span className="text-[9px] text-muted-foreground">+{tags.length - max}</span>}
    </span>
  );
};

const SectionBand = ({ label }) => (
  <div className="py-1 bg-theme-surface/80 border-b border-theme/60">
    <div className="sticky left-0 inline-block px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
      {label}
    </div>
  </div>
);

// ─── Settings popover ────────────────────────────────────────────────
const HoursPopover = ({ startHour, endHour, onSave }) => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(String(startHour));
  const [e, setE] = useState(String(endHour));

  useEffect(() => { setS(String(startHour)); setE(String(endHour)); }, [startHour, endHour, open]);

  const handleSave = async () => {
    const ns = Math.max(0, Math.min(23, parseInt(s, 10) || 0));
    const ne = Math.max(0, Math.min(23, parseInt(e, 10) || 0));
    if (ns >= ne) {
      toast.error("Start hour must be earlier than end hour");
      return;
    }
    await onSave({ start: ns, end: ne });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2.5 text-muted-foreground hover:text-foreground hover:bg-theme-hover"
          data-testid="timeline-hours-btn"
        >
          <Clock className="w-3.5 h-3.5 mr-1.5" />
          <span className="text-xs">{minutesToAmPm(startHour * 60)} – {minutesToAmPm(endHour * 60)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4 bg-card border-border" align="end">
        <h4 className="text-sm font-semibold text-foreground mb-2 font-['Manrope']">Timeline hours</h4>
        <p className="text-[11px] text-muted-foreground mb-3">
          Jobs outside this range auto-expand the view.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[11px] text-muted-foreground">Start (0-23)</label>
            <Input type="number" min={0} max={23} value={s} onChange={(ev) => setS(ev.target.value)}
              className="h-8 text-sm" data-testid="timeline-hours-start"/>
          </div>
          <div className="flex-1">
            <label className="text-[11px] text-muted-foreground">End (0-23)</label>
            <Input type="number" min={0} max={23} value={e} onChange={(ev) => setE(ev.target.value)}
              className="h-8 text-sm" data-testid="timeline-hours-end"/>
          </div>
        </div>
        <Button onClick={handleSave} className="btn-primary w-full mt-3 h-8 text-sm" data-testid="timeline-hours-save">
          Save
        </Button>
      </PopoverContent>
    </Popover>
  );
};

// ─── Row label cell ──────────────────────────────────────────────────
const RowLabel = ({ id, name, role, color }) => (
  <div
    className="flex-shrink-0 flex items-center gap-2.5 px-3 border-r border-theme bg-card sticky left-0 z-20"
    style={{ width: TECH_COL_WIDTH }}
  >
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
      style={{ backgroundColor: color, color: "#fff" }}
    >
      {techInitials(name)}
    </div>
    <div className="min-w-0">
      <div className="text-[13px] font-medium text-foreground truncate">{name}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{role}</div>
    </div>
  </div>
);

// ─── Day view ────────────────────────────────────────────────────────
const DayTimeline = ({
  techs, dispatchRows, jobsByTechByDay, tasksByAssigneeByDay, tagMap, selectedDate,
  startHour, endHour, onPreview, handleEdit, onReschedule, dragging, setDragging,
  onSlotClick, onTaskClick, onCreateSlot,
}) => {
  const dayKey = format(selectedDate, "yyyy-MM-dd");
  const hours = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);
  const colCount = hours.length;
  const totalW = colCount * PIXELS_PER_HOUR_DAY;

  // Single-click opens preview AFTER a short delay so a double-click
  // (edit) can cancel it — otherwise the preview modal's overlay eats
  // the second click and edit never fires.
  const clickTimer = useRef(null);
  const handleBarClick = (job) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => { clickTimer.current = null; onPreview?.(job); }, 250);
  };
  const handleBarDouble = (job) => {
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
    handleEdit?.(job);
  };

  const rowToTime = (clientX, rowEl) => {
    const rect = rowEl.getBoundingClientRect();
    const x = Math.max(0, clientX - rect.left);
    const mins = Math.round((x / rect.width) * colCount * 60);
    const snapped = Math.round(mins / 15) * 15;
    return startHour * 60 + Math.max(0, Math.min(colCount * 60, snapped));
  };

  // ── Drag across empty hours on a technician row → create a job in that
  //    time range. Mouse-only (HTML5 job-bar drags are filtered out).
  const [slotSel, setSlotSel] = useState(null); // {techId, isUnassigned, start, cur, rowEl}
  const selActive = !!slotSel;
  useEffect(() => {
    if (!selActive) return;
    const move = (e) => setSlotSel((s) => (s ? { ...s, cur: rowToTime(e.clientX, s.rowEl) } : s));
    const up = (e) => {
      setSlotSel((s) => {
        if (s) {
          const end = rowToTime(e.clientX, s.rowEl);
          const lo = Math.min(s.start, end);
          const hi = Math.max(s.start, end);
          if (hi - lo >= 15) onCreateSlot?.(s.techId, s.isUnassigned, lo, hi - lo);
        }
        return null;
      });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selActive]);

  const HourGuides = () => (
    <>
      {hours.map((h, hi) => (
        <div key={hi} className="absolute top-0 bottom-0 border-l border-theme/40"
          style={{ left: hi * PIXELS_PER_HOUR_DAY }} />
      ))}
    </>
  );

  return (
    <div className="border border-theme rounded-lg overflow-hidden bg-card">
      <div className="overflow-auto" style={{ maxHeight: "70vh" }} data-testid="timeline-day-scroll">
        <div style={{ minWidth: TECH_COL_WIDTH + totalW }}>
          {/* Hour ruler — frozen while scrolling vertically */}
          <div
            className="flex items-center border-b border-theme bg-theme-surface sticky top-0 z-30"
            style={{ height: HEADER_HEIGHT }}
          >
            <div
              className="flex-shrink-0 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky left-0 z-10 bg-theme-surface h-full flex items-center"
              style={{ width: TECH_COL_WIDTH }}
            >
              Team
            </div>
            {hours.map((h, i) => (
              <div
                key={i}
                style={{ width: PIXELS_PER_HOUR_DAY }}
                className="text-[11px] font-medium text-muted-foreground border-l border-theme/60 px-2 flex items-center"
              >
                {minutesToAmPm(h * 60).replace(":00 ", " ")}
              </div>
            ))}
          </div>

          {/* ── DISPATCH section ── */}
          <SectionBand label="Dispatch tasks" />
          {dispatchRows.map((member) => {
            const memberTasks = (tasksByAssigneeByDay[member.id] || {})[dayKey] || [];
            const taskGeo = memberTasks.map((task) => {
              const tMins = parseTimeStr(task.scheduled_time);
              const effMins = tMins === null ? startHour * 60 : tMins;
              const offsetMins = effMins - startHour * 60;
              if (offsetMins < 0 || offsetMins >= colCount * 60) return null;
              const left = (offsetMins / 60) * PIXELS_PER_HOUR_DAY;
              const dur = Math.max(0.5, parseFloat(task.duration_hours) || 1);
              const width = Math.max(70, dur * PIXELS_PER_HOUR_DAY - 4);
              return { task, left, width, dur };
            }).filter(Boolean);
            const { laneOf, laneCount } = assignLanes(
              taskGeo.map((g) => ({ id: g.task.task_id, left: g.left, width: g.width }))
            );
            const rowH = ROW_PAD_TOP + laneCount * BAR_HEIGHT + (laneCount - 1) * LANE_GAP + ROW_PAD_BOTTOM;
            return (
              <div
                key={member.id}
                className="flex border-b border-theme/60 relative"
                style={{ height: rowH }}
              >
                <RowLabel id={member.id} name={member.name} role={member.role} color={DISPATCH_COLOR} />
                <div
                  className="relative flex-1 cursor-cell"
                  onClick={(e) => {
                    const mins = rowToTime(e.clientX, e.currentTarget);
                    onSlotClick?.(member, dayKey, minutesToAmPm(mins));
                  }}
                  data-testid={`timeline-dispatch-row-${member.id}`}
                >
                  <HourGuides />
                  {taskGeo.map(({ task, left, width, dur }) => {
                    const color = taskColor(task, tagMap);
                    const done = task.status === "done";
                    return (
                      <div
                        key={task.task_id}
                        onClick={(e) => { e.stopPropagation(); onTaskClick?.(task); }}
                        className={`absolute rounded-md px-2 py-1 cursor-pointer border shadow-sm hover:shadow-md transition-all duration-150 overflow-hidden hover:translate-y-[-1px] ${done ? "opacity-55" : ""}`}
                        style={{
                          top: ROW_PAD_TOP + (laneOf[task.task_id] || 0) * (BAR_HEIGHT + LANE_GAP),
                          left: left + 2,
                          width,
                          height: BAR_HEIGHT,
                          backgroundColor: `${color}22`,
                          borderColor: color,
                          borderLeftWidth: 3,
                        }}
                        title={`Task: ${task.title}\n${displayTime(task.scheduled_time) || ""} · ${dur}h${task.notes ? `\n${task.notes}` : ""}`}
                        data-testid={`timeline-task-${task.task_id}`}
                      >
                        <div className={`text-[11px] font-semibold truncate leading-tight flex items-center gap-1 ${done ? "line-through" : ""}`} style={{ color }}>
                          {done && <CheckCircle2 className="w-3 h-3 flex-shrink-0" />}
                          {task.title}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <TagChips task={task} tagMap={tagMap} />
                          <span className="text-[10px] text-muted-foreground/90 truncate">
                            {displayTime(task.scheduled_time)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── TECHNICIANS section ── */}
          <SectionBand label="Technicians" />
          {techs.map((tech) => {
            const techColor = getTechColor(tech.id);
            const techJobs = (jobsByTechByDay[tech.id] || {})[dayKey] || [];
            const isUnassigned = tech.id === "__unassigned__";
            const jobGeo = techJobs.map((job) => {
              const jobMins = parseTimeStr(job.scheduled_time);
              const noTime = jobMins === null;
              const effMins = noTime ? startHour * 60 : jobMins;
              const offsetMins = effMins - startHour * 60;
              if (offsetMins < 0 || offsetMins >= colCount * 60) return null;
              const left = (offsetMins / 60) * PIXELS_PER_HOUR_DAY;
              const dur = Math.max(0.5, parseFloat(job.duration_hours) || 1);
              const width = Math.max(60, dur * PIXELS_PER_HOUR_DAY - 4);
              return { job, noTime, left, width, dur };
            }).filter(Boolean);
            const { laneOf, laneCount } = assignLanes(
              jobGeo.map((g) => ({ id: g.job.job_id, left: g.left, width: g.width }))
            );
            const rowH = ROW_PAD_TOP + laneCount * BAR_HEIGHT + (laneCount - 1) * LANE_GAP + ROW_PAD_BOTTOM;
            return (
              <div
                key={tech.id}
                className="flex border-b border-theme/60 last:border-b-0 relative"
                style={{ height: rowH }}
              >
                <RowLabel id={tech.id} name={tech.name}
                  role={isUnassigned ? "needs assignment" : (tech.role || "tech")}
                  color={techColor.bar} />

                {/* Hour grid + jobs */}
                <div
                  className="relative flex-1 cursor-crosshair"
                  onDragOver={(e) => { e.preventDefault(); }}
                  onMouseDown={(e) => {
                    if (e.button !== 0 || !onCreateSlot) return;
                    if (e.target.closest('[draggable="true"]')) return;
                    e.preventDefault();
                    const mins = rowToTime(e.clientX, e.currentTarget);
                    setSlotSel({ techId: tech.id, isUnassigned, start: mins, cur: mins, rowEl: e.currentTarget });
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!dragging) return;
                    const job = dragging;
                    const newTechId = isUnassigned ? null : tech.id;
                    const newMins = rowToTime(e.clientX, e.currentTarget);
                    onReschedule(job, {
                      assigned_to: newTechId,
                      scheduled_time: minutesToAmPm(newMins),
                      scheduled_date: format(selectedDate, "yyyy-MM-dd"),
                    });
                    setDragging(null);
                  }}
                  data-testid={`timeline-row-${tech.id}`}
                >
                  <HourGuides />

                  {/* Drag-to-create selection highlight */}
                  {slotSel && slotSel.techId === tech.id && (() => {
                    const lo = Math.min(slotSel.start, slotSel.cur);
                    const hi = Math.max(slotSel.start, slotSel.cur);
                    const selLeft = ((lo - startHour * 60) / 60) * PIXELS_PER_HOUR_DAY;
                    const selWidth = Math.max(3, ((hi - lo) / 60) * PIXELS_PER_HOUR_DAY);
                    return (
                      <div
                        className="absolute top-1 bottom-1 rounded-md border-2 border-dashed border-[#FFCC00] bg-[#FFCC00]/15 pointer-events-none z-10 flex items-center justify-center"
                        style={{ left: selLeft, width: selWidth }}
                        data-testid="timeline-slot-selection"
                      >
                        {selWidth > 80 && (
                          <span className="text-[10px] font-bold text-black bg-[#FFCC00] px-1.5 py-0.5 rounded whitespace-nowrap shadow">
                            {minutesToAmPm(lo)} – {minutesToAmPm(hi)}
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Job bars */}
                  {jobGeo.map(({ job, noTime, left, width, dur }) => {
                    return (
                      <div
                        key={job.job_id}
                        draggable
                        onDragStart={(e) => {
                          setDragging(job);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", job.job_id);
                        }}
                        onDragEnd={() => setDragging(null)}
                        onClick={(e) => { e.stopPropagation(); handleBarClick(job); }}
                        onDoubleClick={(e) => { e.stopPropagation(); handleBarDouble(job); }}
                        className={`absolute rounded-md px-2 py-1 cursor-grab active:cursor-grabbing border shadow-sm hover:shadow-md transition-all duration-150 overflow-hidden hover:translate-y-[-1px] ${noTime ? "border-dashed" : ""}`}
                        style={{
                          top: ROW_PAD_TOP + (laneOf[job.job_id] || 0) * (BAR_HEIGHT + LANE_GAP),
                          left: left + 2,
                          width,
                          height: BAR_HEIGHT,
                          backgroundColor: techColor.bg,
                          borderColor: techColor.border,
                          borderLeftWidth: 3,
                        }}
                        title={`${job.customer_name || "Customer"}\n${job.customer_address || ""}\n${job.title || ""}\n${displayTime(job.scheduled_time) || "no time set"} · ${dur}h`}
                        data-testid={`timeline-job-${job.job_id}`}
                      >
                        <div className="text-[11px] font-semibold truncate leading-tight" style={{ color: techColor.border }}>
                          {jobShortLabel(job)}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock className="w-2.5 h-2.5 text-muted-foreground/80" />
                          <span className="text-[10px] text-muted-foreground/90 truncate">
                            {noTime ? "no time" : displayTime(job.scheduled_time)} · {dur}h
                          </span>
                          {job.customer_address && (
                            <>
                              <MapPin className="w-2.5 h-2.5 text-muted-foreground/60 ml-1" />
                              <span className="text-[10px] text-muted-foreground/80 truncate">
                                {(job.customer_address || "").split(",")[0]}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Week view ───────────────────────────────────────────────────────
const WeekTimeline = ({
  techs, dispatchRows, jobsByTechByDay, tasksByAssigneeByDay, tagMap, weekStart,
  sortDir = 1,
  onPreview, handleEdit, onReschedule, dragging, setDragging, onSlotClick, onTaskClick,
}) => {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const DAY_COL_W = 180;
  const totalW = 7 * DAY_COL_W;

  // Same click-vs-double-click debounce as the Day view.
  const clickTimer = useRef(null);
  const handleBarClick = (job) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => { clickTimer.current = null; onPreview?.(job); }, 250);
  };
  const handleBarDouble = (job) => {
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
    handleEdit?.(job);
  };

  return (
    <div className="border border-theme rounded-lg overflow-hidden bg-card">
      <div className="overflow-auto" style={{ maxHeight: "70vh" }} data-testid="timeline-week-scroll">
        <div style={{ minWidth: TECH_COL_WIDTH + totalW }}>
          {/* Day header — frozen while scrolling vertically */}
          <div className="flex items-center border-b border-theme bg-theme-surface sticky top-0 z-30" style={{ height: HEADER_HEIGHT }}>
            <div className="flex-shrink-0 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky left-0 z-10 bg-theme-surface h-full flex items-center"
              style={{ width: TECH_COL_WIDTH }}>
              Team
            </div>
            {days.map((d, i) => (
              <div
                key={i}
                style={{ width: DAY_COL_W }}
                className="text-[11px] font-medium text-foreground border-l border-theme/60 px-2.5 flex flex-col justify-center"
              >
                <span className="uppercase tracking-wide text-muted-foreground">{format(d, "EEE")}</span>
                <span className="text-[12px] font-semibold">{format(d, "MMM d")}</span>
              </div>
            ))}
          </div>

          {/* ── DISPATCH section ── */}
          <SectionBand label="Dispatch tasks" />
          {dispatchRows.map((member) => (
            <div key={member.id} className="flex border-b border-theme/60" style={{ minHeight: ROW_HEIGHT }}>
              <RowLabel id={member.id} name={member.name} role={member.role} color={DISPATCH_COLOR} />
              {days.map((d, di) => {
                const dKey = format(d, "yyyy-MM-dd");
                const dayTasks = (tasksByAssigneeByDay[member.id] || {})[dKey] || [];
                return (
                  <div
                    key={di}
                    className="border-l border-theme/40 p-1 flex flex-col gap-1 cursor-cell"
                    style={{ width: DAY_COL_W }}
                    onClick={() => onSlotClick?.(member, dKey, "9:00 AM")}
                    data-testid={`timeline-week-dispatch-cell-${member.id}-${dKey}`}
                  >
                    {dayTasks
                      .slice()
                      .sort((a, b) => sortDir * ((parseTimeStr(a.scheduled_time) ?? 0) - (parseTimeStr(b.scheduled_time) ?? 0)))
                      .map((task) => {
                        const color = taskColor(task, tagMap);
                        const done = task.status === "done";
                        return (
                          <div
                            key={task.task_id}
                            onClick={(e) => { e.stopPropagation(); onTaskClick?.(task); }}
                            className={`rounded px-1.5 py-1 cursor-pointer border text-[11px] overflow-hidden hover:translate-y-[-1px] transition-transform ${done ? "opacity-55" : ""}`}
                            style={{
                              backgroundColor: `${color}22`,
                              borderColor: color,
                              borderLeftWidth: 3,
                            }}
                            title={`Task: ${task.title}\n${displayTime(task.scheduled_time) || ""}${task.notes ? `\n${task.notes}` : ""}`}
                            data-testid={`timeline-week-task-${task.task_id}`}
                          >
                            <div className={`font-semibold truncate flex items-center gap-1 ${done ? "line-through" : ""}`} style={{ color }}>
                              {done && <CheckCircle2 className="w-3 h-3 flex-shrink-0" />}
                              {task.title}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <TagChips task={task} tagMap={tagMap} max={2} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          ))}

          {/* ── TECHNICIANS section ── */}
          <SectionBand label="Technicians" />
          {techs.map((tech) => {
            const techColor = getTechColor(tech.id);
            const isUnassigned = tech.id === "__unassigned__";
            return (
              <div
                key={tech.id}
                className="flex border-b border-theme/60 last:border-b-0"
                style={{ minHeight: ROW_HEIGHT }}
              >
                <RowLabel id={tech.id} name={tech.name}
                  role={isUnassigned ? "needs assignment" : (tech.role || "tech")}
                  color={techColor.bar} />

                {/* Day cells */}
                {days.map((d, di) => {
                  const dKey = format(d, "yyyy-MM-dd");
                  const dayJobs = (jobsByTechByDay[tech.id] || {})[dKey] || [];
                  return (
                    <div
                      key={di}
                      className="border-l border-theme/40 p-1 flex flex-col gap-1"
                      style={{ width: DAY_COL_W }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragging) return;
                        onReschedule(dragging, {
                          assigned_to: isUnassigned ? null : tech.id,
                          scheduled_date: dKey,
                          scheduled_time: dragging.scheduled_time || "9:00 AM",
                        });
                        setDragging(null);
                      }}
                      data-testid={`timeline-week-cell-${tech.id}-${dKey}`}
                    >
                      {dayJobs
                        .slice()
                        .sort((a, b) => sortDir * ((parseTimeStr(a.scheduled_time) ?? 0) - (parseTimeStr(b.scheduled_time) ?? 0)))
                        .map((job) => (
                        <div
                          key={job.job_id}
                          draggable
                          onDragStart={(e) => {
                            setDragging(job);
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", job.job_id);
                          }}
                          onDragEnd={() => setDragging(null)}
                          onClick={(e) => { e.stopPropagation(); handleBarClick(job); }}
                          onDoubleClick={(e) => { e.stopPropagation(); handleBarDouble(job); }}
                          className="rounded px-1.5 py-1 cursor-grab active:cursor-grabbing border text-[11px] overflow-hidden hover:translate-y-[-1px] transition-transform"
                          style={{
                            backgroundColor: techColor.bg,
                            borderColor: techColor.border,
                            borderLeftWidth: 3,
                          }}
                          title={`${job.customer_name || "Customer"}\n${job.customer_address || ""}\n${displayTime(job.scheduled_time) || ""}`}
                          data-testid={`timeline-week-job-${job.job_id}`}
                        >
                          <div className="font-semibold truncate" style={{ color: techColor.border }}>
                            {displayTime(job.scheduled_time) || "no time"} · {job.customer_name || "Customer"}
                          </div>
                          {job.customer_address && (
                            <div className="text-muted-foreground truncate">
                              {(job.customer_address || "").split(",")[0]}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main exported component ────────────────────────────────────────
const JobTimelineView = ({
  jobs,
  teamMembers,
  selectedDate,
  setSelectedDate,
  weekStart,
  setWeekStart,
  onPreview,
  handleEdit,
  onJobsChanged,
  hoursPref,
  saveHoursPref,
  onCreateJob,
}) => {
  const { getToday } = useTimezone();
  const [mode, setMode] = useState("day");
  const [dragging, setDragging] = useState(null);
  // Week-cell time-sort — shared preference with the Calendar/List views.
  // Timeline week cells historically show earliest-first, so that's the default.
  const [sortOrder, setSortOrder] = useState(() => readJobSortPref() || "asc");
  const toggleSort = () => {
    const next = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(next);
    writeJobSortPref(next);
  };

  // Dispatcher tasks + tags + office users
  const [tasks, setTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [officeMembers, setOfficeMembers] = useState([]);
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null, defaults: null });
  const [panelOpen, setPanelOpen] = useState(false);
  const [todayOpenCount, setTodayOpenCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const tasksReqId = useRef(0);

  const tagMap = useMemo(() => Object.fromEntries((tags || []).map((t) => [t.tag_id, t])), [tags]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dispatch/tags`, getAuthConfig());
      setTags(Array.isArray(res.data) ? res.data : []);
    } catch { /* tags are non-critical */ }
  }, []);

  const fetchOfficeMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`, getAuthConfig());
      setOfficeMembers((Array.isArray(res.data) ? res.data : []).filter((m) => m.status !== "inactive" && m.status !== "disabled"));
    } catch { /* silent */ }
  }, []);

  const windowRange = useMemo(() => {
    if (mode === "day") {
      const k = format(selectedDate, "yyyy-MM-dd");
      return { start: k, end: k };
    }
    return {
      start: format(weekStart, "yyyy-MM-dd"),
      end: format(addDays(weekStart, 6), "yyyy-MM-dd"),
    };
  }, [mode, selectedDate, weekStart]);

  const fetchTasks = useCallback(async () => {
    const reqId = ++tasksReqId.current;
    try {
      const res = await axios.get(
        `${API}/dispatch/tasks?start_date=${windowRange.start}&end_date=${windowRange.end}`,
        getAuthConfig()
      );
      if (reqId === tasksReqId.current) setTasks(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
  }, [windowRange]);

  const fetchTodayCount = useCallback(async () => {
    try {
      const todayKey = format(getToday(), "yyyy-MM-dd");
      const res = await axios.get(
        `${API}/dispatch/tasks?start_date=${todayKey}&end_date=${todayKey}`,
        getAuthConfig()
      );
      setTodayOpenCount((Array.isArray(res.data) ? res.data : []).filter((t) => t.status !== "done").length);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTags(); fetchOfficeMembers(); }, [fetchTags, fetchOfficeMembers]);
  useEffect(() => { fetchTasks(); }, [fetchTasks, refreshKey]);
  useEffect(() => { fetchTodayCount(); }, [fetchTodayCount, refreshKey]);

  const onTasksChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  // ── Dispatch rows: Office/Anyone + every active office user; tasks
  //    assigned to unknown ids still get a fallback row so nothing hides.
  const dispatchRows = useMemo(() => {
    const base = [
      { id: "__dispatch_unassigned__", name: "Office / Anyone", role: "shared tasks" },
      ...officeMembers.map((m) => ({ id: m.member_id, name: m.name, role: m.role || "dispatcher" })),
    ];
    const known = new Set(base.map((r) => r.id));
    const extras = new Map();
    (tasks || []).forEach((t) => {
      if (t.assigned_to && !known.has(t.assigned_to) && !extras.has(t.assigned_to)) {
        extras.set(t.assigned_to, { id: t.assigned_to, name: "Former member", role: "removed" });
      }
    });
    return [...base, ...extras.values()];
  }, [officeMembers, tasks]);

  const assigneeNames = useMemo(
    () => Object.fromEntries(dispatchRows.map((r) => [r.id, r.name])),
    [dispatchRows]
  );

  // ── Tech list: Unassigned + ALL active technicians (always shown) ────
  const techs = useMemo(() => {
    const active = (teamMembers || []).filter(
      (m) => m.status !== "disabled" && m.status !== "inactive"
    );
    const base = [
      { id: "__unassigned__", name: "Unassigned", role: "needs assignment" },
      ...active.map((t) => ({
        id: t.member_id || t.technician_id,
        name: t.name,
        role: t.role || "tech",
      })),
    ];
    const known = new Set(base.map((t) => t.id));
    const extras = new Map();
    (jobs || []).forEach((j) => {
      if (j.assigned_to && !known.has(j.assigned_to) && !extras.has(j.assigned_to)) {
        extras.set(j.assigned_to, {
          id: j.assigned_to,
          name: j.assigned_to_name || "Removed technician",
          role: "inactive / removed",
        });
      }
      (j.additional_technician_ids || []).forEach((tid, i) => {
        if (tid && !known.has(tid) && !extras.has(tid)) {
          extras.set(tid, {
            id: tid,
            name: (j.additional_technician_names || [])[i] || "Removed technician",
            role: "inactive / removed",
          });
        }
      });
    });
    return [...base, ...extras.values()];
  }, [teamMembers, jobs]);

  // ── Group jobs by tech_id → day_key (timezone-safe). Multi-technician
  //    jobs are duplicated into EVERY assigned tech's lane. ─────────────
  const jobsByTechByDay = useMemo(() => {
    const out = {};
    const push = (techKey, dKey, j) => {
      if (!out[techKey]) out[techKey] = {};
      if (!out[techKey][dKey]) out[techKey][dKey] = [];
      out[techKey][dKey].push(j);
    };
    (jobs || []).forEach((j) => {
      const dKey = dateKeyOf(j.scheduled_date);
      if (!dKey) return;
      push(j.assigned_to || "__unassigned__", dKey, j);
      (j.additional_technician_ids || []).forEach((tid) => {
        if (tid && tid !== j.assigned_to) push(tid, dKey, j);
      });
    });
    return out;
  }, [jobs]);

  const tasksByAssigneeByDay = useMemo(() => {
    const out = {};
    (tasks || []).forEach((t) => {
      if (t.status === "done") return; // completed tasks leave the timeline
      const dKey = dateKeyOf(t.scheduled_date);
      if (!dKey) return;
      const key = t.assigned_to || "__dispatch_unassigned__";
      if (!out[key]) out[key] = {};
      if (!out[key][dKey]) out[key][dKey] = [];
      out[key][dKey].push(t);
    });
    return out;
  }, [tasks]);

  // ── Compute hour range: pinned setting OR auto-fit (jobs + tasks) ────
  const { startHour, endHour } = useMemo(() => {
    let start = hoursPref?.start_hour ?? 7;
    let end = hoursPref?.end_hour ?? 19;
    const dKey = format(selectedDate, "yyyy-MM-dd");
    const expand = (dateVal, timeVal, durVal) => {
      if (dateKeyOf(dateVal) !== dKey) return;
      const mins = parseTimeStr(timeVal);
      if (mins === null) return;
      const h = Math.floor(mins / 60);
      const dur = Math.max(0.5, parseFloat(durVal) || 1);
      const endH = Math.ceil(h + dur);
      if (h < start) start = Math.max(0, h);
      if (endH > end) end = Math.min(23, endH);
    };
    (jobs || []).forEach((j) => expand(j.scheduled_date, j.scheduled_time, j.duration_hours));
    (tasks || []).forEach((t) => expand(t.scheduled_date, t.scheduled_time, t.duration_hours));
    if (start >= end) end = start + 1;
    return { startHour: start, endHour: end };
  }, [hoursPref, selectedDate, jobs, tasks]);

  // ── Drag-drop → PUT /api/jobs/{id} ──────────────────────────────────
  const handleReschedule = useCallback(async (job, patch) => {
    const sameTech = (patch.assigned_to || null) === (job.assigned_to || null);
    const sameTime = patch.scheduled_time === job.scheduled_time;
    const sameDate = patch.scheduled_date === dateKeyOf(job.scheduled_date);
    if (sameTech && sameTime && sameDate) return;

    try {
      await axios.put(`${API}/jobs/${job.job_id}`, {
        ...job,
        assigned_to: patch.assigned_to || "",
        scheduled_time: patch.scheduled_time,
        scheduled_date: patch.scheduled_date,
      }, getAuthConfig());
      toast.success("Job rescheduled");
      onJobsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reschedule");
    }
  }, [onJobsChanged]);

  // ── Drag-to-create → open the job form pre-filled with the slot ──────
  const handleCreateSlot = useCallback((techId, isUnassigned, startMins, durMins) => {
    const dur = Math.max(0.5, Math.round((durMins / 60) * 4) / 4);
    onCreateJob?.({
      scheduled_date: format(selectedDate, "yyyy-MM-dd"),
      scheduled_time: minutesToAmPm(startMins),
      duration_hours: dur,
      assigned_to: isUnassigned ? "" : techId,
    });
  }, [onCreateJob, selectedDate]);

  // ── Task interactions ────────────────────────────────────────────────
  const openCreateTask = useCallback((member, dKey, timeStr) => {
    setTaskDialog({
      open: true,
      task: null,
      defaults: {
        assigned_to: member && member.id !== "__dispatch_unassigned__" ? member.id : "",
        scheduled_date: dKey,
        scheduled_time: timeStr || "9:00 AM",
      },
    });
  }, []);

  const openEditTask = useCallback((task) => {
    setTaskDialog({ open: true, task, defaults: null });
  }, []);

  // ── Navigation ──────────────────────────────────────────────────────
  const goPrev = () => {
    if (mode === "day") setSelectedDate(subDays(selectedDate, 1));
    else setWeekStart(subDays(weekStart, 7));
  };
  const goNext = () => {
    if (mode === "day") setSelectedDate(addDays(selectedDate, 1));
    else setWeekStart(addDays(weekStart, 7));
  };
  const goToday = () => {
    const today = getToday();
    if (mode === "day") setSelectedDate(today);
    else setWeekStart(startOfWeek(today, { weekStartsOn: 1 }));
  };

  return (
    <div className="space-y-3" data-testid="job-timeline-view">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goPrev}
            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-theme-hover"
            data-testid="timeline-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToday}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-theme-hover"
            data-testid="timeline-today-btn">
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={goNext}
            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-theme-hover"
            data-testid="timeline-next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="text-sm font-semibold text-foreground ml-1 font-['Manrope']">
            {mode === "day"
              ? format(selectedDate, "EEE, MMM d, yyyy")
              : `${format(weekStart, "MMM d")} – ${format(addDays(weekStart, 6), "MMM d, yyyy")}`}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setPanelOpen(true)}
            className="h-8 px-3 text-xs bg-theme-surface border border-theme text-foreground hover:bg-theme-hover relative"
            variant="ghost"
            data-testid="timeline-tasks-panel-btn">
            <ClipboardList className="w-3.5 h-3.5 mr-1" /> Tasks
            {todayOpenCount > 0 && (
              <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FFCC00] text-black text-[10px] font-bold flex items-center justify-center"
                data-testid="tasks-today-badge">
                {todayOpenCount}
              </span>
            )}
          </Button>

          <Button size="sm" onClick={() => openCreateTask(null, format(mode === "day" ? selectedDate : weekStart, "yyyy-MM-dd"), "9:00 AM")}
            className="h-8 px-3 text-xs bg-theme-surface border border-theme text-foreground hover:bg-theme-hover"
            variant="ghost"
            data-testid="timeline-new-task-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Task
          </Button>

          {/* Day/Week sub-toggle */}
          <div className="flex bg-theme-surface rounded-lg p-1">
            <Button variant="ghost" size="sm" onClick={() => setMode("day")}
              className={`h-7 px-3 text-xs ${mode === "day" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
              data-testid="timeline-mode-day">
              Day
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode("week")}
              className={`h-7 px-3 text-xs ${mode === "week" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
              data-testid="timeline-mode-week">
              Week
            </Button>
          </div>

          {mode === "week" && (
            <JobSortToggle sortOrder={sortOrder} onToggle={toggleSort} testId="timeline-sort-toggle" />
          )}

          {mode === "day" && (
            <HoursPopover
              startHour={startHour}
              endHour={endHour}
              onSave={async ({ start, end }) => {
                await saveHoursPref(start, end);
              }}
            />
          )}
        </div>
      </div>

      {mode === "day" ? (
        <DayTimeline
          techs={techs}
          dispatchRows={dispatchRows}
          jobsByTechByDay={jobsByTechByDay}
          tasksByAssigneeByDay={tasksByAssigneeByDay}
          tagMap={tagMap}
          selectedDate={selectedDate}
          startHour={startHour}
          endHour={endHour}
          onPreview={onPreview}
          handleEdit={handleEdit}
          onReschedule={handleReschedule}
          dragging={dragging}
          setDragging={setDragging}
          onSlotClick={openCreateTask}
          onTaskClick={openEditTask}
          onCreateSlot={onCreateJob ? handleCreateSlot : undefined}
        />
      ) : (
        <WeekTimeline
          techs={techs}
          dispatchRows={dispatchRows}
          jobsByTechByDay={jobsByTechByDay}
          tasksByAssigneeByDay={tasksByAssigneeByDay}
          tagMap={tagMap}
          weekStart={weekStart}
          sortDir={sortOrder === "asc" ? 1 : -1}
          onPreview={onPreview}
          handleEdit={handleEdit}
          onReschedule={handleReschedule}
          dragging={dragging}
          setDragging={setDragging}
          onSlotClick={openCreateTask}
          onTaskClick={openEditTask}
        />
      )}

      <p className="text-[11px] text-muted-foreground/70 text-center pt-1">
        Tip: drag across empty hours on a technician's row to create a job in that time slot · click an empty slot on a dispatcher's row to create a task · drag a job to reschedule · click a job to preview, double-click to edit.
      </p>

      <DispatchTaskDialog
        open={taskDialog.open}
        task={taskDialog.task}
        defaults={taskDialog.defaults}
        tags={tags}
        assignees={dispatchRows.filter((r) => r.id !== "__dispatch_unassigned__" && r.role !== "removed")}
        onClose={() => setTaskDialog({ open: false, task: null, defaults: null })}
        onSaved={onTasksChanged}
        onTagsChanged={fetchTags}
      />

      <DispatchTasksPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        tags={tags}
        assigneeNames={assigneeNames}
        refreshKey={refreshKey}
        onChanged={onTasksChanged}
        onEditTask={(task) => openEditTask(task)}
        onCreateTask={() => openCreateTask(null, format(getToday(), "yyyy-MM-dd"), "9:00 AM")}
      />
    </div>
  );
};

export default JobTimelineView;
